var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/schemas/analytics.schema.ts
import { z } from "zod";
var code = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/);
var version = z.string().min(1).max(64).regex(/^[a-zA-Z0-9][a-zA-Z0-9._-]*$/);
var host = z.string().min(1).max(253).regex(/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/);
var analyticsGroupSchema = z.enum([
  "public",
  "product",
  "operations"
]);
var analyticsSurfaceSchema = z.enum([
  "web",
  "ios",
  "android"
]);
var analyticsAppModuleSchema = z.enum([
  "landing",
  "docs",
  "admin",
  "employee_app",
  "system_admin",
  "hr_system",
  "team_manager",
  "module_manager",
  "general_affairs",
  "inventory",
  "drive",
  "management_dashboard"
]);
var analyticsInteractionNameSchema = z.enum([
  "login",
  "workspace_selected",
  "cta_click",
  "generate_lead",
  "guide_open",
  "resource_click",
  "product_link_click"
]);
var analyticsErrorCategorySchema = z.enum([
  "validation",
  "permission",
  "network",
  "conflict",
  "server",
  "unknown"
]);
var analyticsDestinationSchema = z.object({
  group: analyticsGroupSchema,
  gtmId: z.string().regex(/^GTM-[A-Z0-9]{4,20}$/),
  measurementId: z.string().regex(/^G-[A-Z0-9]{4,20}$/)
});
var screen = z.object({
  id: code,
  title: z.string().min(1).max(100),
  path: z.string().max(200).regex(/^\/(?:[a-zA-Z0-9_-]+\/)*[a-zA-Z0-9_.-]*$/).refine((value) => !value.split("/").some((part) => part === "." || part === ".."))
});
var codes = z.array(code).max(100);
var analyticsConfigSchema = z.object({
  enabled: z.boolean().default(false),
  group: analyticsGroupSchema,
  appModule: analyticsAppModuleSchema,
  surface: analyticsSurfaceSchema,
  deploymentEnvironment: z.enum([
    "production",
    "development",
    "test"
  ]),
  consentPolicyVersion: version,
  releaseVersion: version,
  productionHosts: z.array(host).min(1).max(100),
  canonicalHost: host,
  productionDestination: analyticsDestinationSchema,
  productionDestinationRegistry: z.object({
    public: analyticsDestinationSchema,
    product: analyticsDestinationSchema,
    operations: analyticsDestinationSchema
  }).optional(),
  destinationMode: z.enum([
    "production",
    "test"
  ]).default("production"),
  testDestination: analyticsDestinationSchema.optional(),
  testHosts: z.array(host).max(100).default([]),
  nativeEnvironmentVerified: z.boolean().default(false),
  screens: z.array(screen).min(1),
  workflows: codes.default([]),
  eventCodes: z.object({
    login: codes.optional(),
    workspace_selected: codes.optional(),
    cta_click: codes.optional(),
    generate_lead: codes.optional(),
    guide_open: codes.optional(),
    resource_click: codes.optional(),
    product_link_click: codes.optional()
  }).default({}),
  tenantPolicy: z.object({
    enabled: z.literal(true),
    verified: z.literal(true)
  }).optional()
}).superRefine((config, ctx) => {
  const validGroup = config.group === "public" ? [
    "landing",
    "docs"
  ].includes(config.appModule) : config.group === "product" ? ![
    "landing",
    "docs",
    "system_admin"
  ].includes(config.appModule) : config.appModule === "system_admin";
  const safeHost = /* @__PURE__ */ __name((value) => value.includes(".") && !/^(?:\d+\.){3}\d+$/.test(value) && !/(^|[.-])(localhost|local|dev|development|staging|test|preview)([.-]|$)/.test(value), "safeHost");
  if (!validGroup || config.productionDestination.group !== config.group || !config.productionHosts.every(safeHost) || !config.productionHosts.includes(config.canonicalHost) || new Set(config.screens.map((value) => value.id)).size !== config.screens.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "\uBD84\uC11D \uC124\uC815\uC744 \uD655\uC778\uD574 \uC8FC\uC138\uC694."
    });
  }
  if (config.destinationMode === "production" && config.deploymentEnvironment !== "production") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "\uC6B4\uC601 \uD658\uACBD\uC744 \uD655\uC778\uD574 \uC8FC\uC138\uC694."
    });
  }
  if (config.destinationMode === "test") {
    const registry = config.productionDestinationRegistry;
    const target = config.testDestination;
    if (!(registry && target) || Object.entries(registry).some(([group, destination]) => destination.group !== group) || registry[config.group].gtmId !== config.productionDestination.gtmId || registry[config.group].measurementId !== config.productionDestination.measurementId || Object.values(registry).some((destination) => destination.gtmId === target.gtmId || destination.measurementId === target.measurementId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "\uC6B4\uC601 \uC804\uC1A1 \uB300\uC0C1 \uBAA9\uB85D\uC744 \uD655\uC778\uD574 \uC8FC\uC138\uC694."
      });
    }
  }
  if (config.destinationMode === "test" && (!config.testDestination || config.testDestination.group !== config.group || config.testDestination.gtmId === config.productionDestination.gtmId || config.testDestination.measurementId === config.productionDestination.measurementId)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "\uD14C\uC2A4\uD2B8 \uC804\uC1A1 \uB300\uC0C1\uC744 \uD655\uC778\uD574 \uC8FC\uC138\uC694."
    });
  }
});
var analyticsTenantKeySchema = z.string().regex(/^t_[a-zA-Z0-9_-]{20,80}$/);
var analyticsEventSchema = z.object({
  event_name: z.enum([
    "page_view",
    "screen_view",
    "login",
    "workspace_selected",
    "cta_click",
    "generate_lead",
    "guide_open",
    "resource_click",
    "product_link_click",
    "workflow_started",
    "workflow_validation_failed",
    "workflow_submitted",
    "workflow_completed",
    "workflow_failed",
    "workflow_unconfirmed"
  ]),
  params: z.object({
    app_module: analyticsAppModuleSchema,
    surface: analyticsSurfaceSchema,
    ui_screen: code,
    workflow: code.optional(),
    result: z.enum([
      "started",
      "validation_failed",
      "submitted",
      "completed",
      "failed",
      "unconfirmed"
    ]).optional(),
    error_category: analyticsErrorCategorySchema.optional(),
    schema_version: z.literal(1),
    release_version: version,
    duration_ms: z.number().finite().int().min(0).max(864e5).optional(),
    interaction_code: code.optional(),
    tenant_key: analyticsTenantKeySchema.optional(),
    page_location: z.string().url().max(500),
    page_title: z.string().max(100),
    page_referrer: z.union([
      z.literal(""),
      z.string().url().max(500)
    ])
  })
});
var analyticsPublicSettingsSchema = z.object({
  enabled: z.boolean(),
  destinationMode: z.enum([
    "production",
    "test"
  ]).default("production"),
  testHosts: z.array(host).max(100).default([]),
  testDestination: analyticsDestinationSchema.optional(),
  productionDestinationRegistry: z.object({
    public: analyticsDestinationSchema,
    product: analyticsDestinationSchema,
    operations: analyticsDestinationSchema
  }).optional(),
  deploymentEnvironment: z.enum([
    "production",
    "development",
    "test"
  ]),
  consentPolicyVersion: version,
  releaseVersion: version,
  productionHosts: z.array(host).min(1).max(100),
  canonicalHost: host,
  gtmId: z.string().regex(/^GTM-[A-Z0-9]{4,20}$/),
  measurementId: z.string().regex(/^G-[A-Z0-9]{4,20}$/)
});
function readAnalyticsPublicSettings(env, prefix = "VITE_ANALYTICS_") {
  const hosts = env[`${prefix}PRODUCTION_HOSTS`];
  const parsed = analyticsPublicSettingsSchema.safeParse({
    enabled: env[`${prefix}ENABLED`] === "true",
    deploymentEnvironment: env[`${prefix}DEPLOYMENT_ENVIRONMENT`],
    consentPolicyVersion: env[`${prefix}CONSENT_POLICY_VERSION`],
    releaseVersion: env[`${prefix}RELEASE_VERSION`],
    productionHosts: typeof hosts === "string" ? hosts.split(",").map((value) => value.trim()).filter(Boolean) : [],
    canonicalHost: env[`${prefix}CANONICAL_HOST`],
    gtmId: env[`${prefix}GTM_ID`],
    measurementId: env[`${prefix}MEASUREMENT_ID`]
  });
  return parsed.success ? parsed.data : null;
}
__name(readAnalyticsPublicSettings, "readAnalyticsPublicSettings");
var analyticsConsentRecordSchema = z.object({
  version: z.literal(1),
  choice: z.enum([
    "granted",
    "denied"
  ])
}).strict();
var analyticsFrameMessageSchema = z.discriminatedUnion("type", [
  z.object({
    protocol: z.literal("hereby-analytics"),
    version: z.literal(1),
    type: z.literal("hello"),
    module: analyticsAppModuleSchema
  }).strict(),
  z.object({
    protocol: z.literal("hereby-analytics"),
    version: z.literal(1),
    type: z.literal("event"),
    module: analyticsAppModuleSchema,
    epoch: z.number().int().nonnegative(),
    event: analyticsEventSchema
  }).strict(),
  z.object({
    protocol: z.literal("hereby-analytics"),
    version: z.literal(1),
    type: z.literal("state"),
    module: analyticsAppModuleSchema,
    epoch: z.number().int().nonnegative(),
    granted: z.boolean(),
    active: z.boolean()
  }).strict()
]);
var analyticsNativeBuildSchema = z.object({
  projectId: z.string().min(1).max(100),
  appId: z.literal("me.hereby.works"),
  collectionAllowed: z.boolean()
});
var analyticsNativeConsentSchema = z.object({
  version: z.literal(1),
  scope: z.string().min(1).max(200),
  choice: z.enum([
    "granted",
    "denied"
  ]).nullable()
});
export {
  analyticsAppModuleSchema,
  analyticsConfigSchema,
  analyticsConsentRecordSchema,
  analyticsDestinationSchema,
  analyticsErrorCategorySchema,
  analyticsEventSchema,
  analyticsFrameMessageSchema,
  analyticsGroupSchema,
  analyticsInteractionNameSchema,
  analyticsNativeBuildSchema,
  analyticsNativeConsentSchema,
  analyticsPublicSettingsSchema,
  analyticsSurfaceSchema,
  analyticsTenantKeySchema,
  readAnalyticsPublicSettings
};
//# sourceMappingURL=analytics.mjs.map